/* Native Shopify localization; motion stays inside this optional product carousel. */
if (!customElements.get('waggl-localization')) {
  customElements.define('waggl-localization', class extends HTMLElement {
    connectedCallback() {
      if (this.onChange) return;
      this.onChange = event => {
        const form = event.target.closest('form');
        if (!event.target.matches('select') || !form) return;
        if (form.requestSubmit) form.requestSubmit();
        else form.submit();
      };
      this.addEventListener('change', this.onChange);
    }
    disconnectedCallback() {this.removeEventListener('change', this.onChange); this.onChange = null;}
  });
}

// 1.3.2: continuous, constant-speed motion; no dwell timer or opacity swaps.
if (!customElements.get('waggl-carousel')) customElements.define('waggl-carousel', class extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.track = this.querySelector('[data-wg-carousel-track]');
    this.viewport = this.querySelector('[data-wg-carousel-viewport]');
    this.controls = this.querySelector('[data-wg-carousel-controls]');
    if (!this.track || !this.viewport) return;
    this.ready = true; this.run = (this.run || 0) + 1; this.listeners = [];
    this.position = 0; this.cycle = 0; this.frame = 0; this.lastTime = 0;
    this.hovered = false; this.keyboardFocused = false; this.keyboardMode = false;
    this.dragging = false; this.touching = false; this.editorSelected = false;
    this.inView = true; this.resumeAt = 0; this.pendingItems = null;
    this.mobile = window.matchMedia('(max-width:749px)');
    this.reduced = window.matchMedia('(prefers-reduced-motion:reduce)');
    this.listen(this, 'pointerenter', event => {
      if (event.pointerType === 'touch') return;
      this.hovered = true; this.tween = null; this.updateMotion();
    });
    this.listen(this, 'pointerleave', () => { this.hovered = false; this.applyPending(); this.updateMotion(); });
    this.listen(document, 'keydown', event => { if (event.key === 'Tab') this.keyboardMode = true; });
    this.listen(this, 'pointerdown', () => { this.keyboardMode = false; this.keyboardFocused = false; });
    this.listen(this, 'focusin', event => {
      this.keyboardFocused = this.keyboardMode;
      const copy = event.target.closest('[data-wg-clone]');
      if (copy) {
        const original = this.items.find(item => item.dataset.wgProductKey === copy.dataset.wgProductKey);
        const selector = 'a[href],button,input,select,textarea,[tabindex]';
        const index = [...copy.querySelectorAll(selector)].indexOf(event.target);
        original?.querySelectorAll(selector)[index]?.focus({ preventScroll: true });
      }
      this.updateMotion();
    });
    this.listen(this, 'focusout', () => {
      window.clearTimeout(this.focusTimer);
      this.focusTimer = window.setTimeout(() => {
        this.keyboardFocused = this.contains(document.activeElement) && this.keyboardMode;
        this.applyPending(); this.updateMotion();
      }, 0);
    });
    this.listen(this, 'click', event => {
      const arrow = event.target.closest('[data-wg-carousel-step]');
      if (arrow) this.move(Number(arrow.dataset.wgCarouselStep));
    });
    this.listen(this.viewport, 'keydown', event => {
      if (event.target !== this.viewport || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault(); this.move(event.key === 'ArrowLeft' ? -1 : 1);
    });
    this.listen(this.viewport, 'pointerdown', () => { this.dragging = true; this.tween = null; this.updateMotion(); });
    const release = () => { this.dragging = false; this.applyPending(); this.updateMotion(); };
    this.listen(document, 'pointerup', release); this.listen(document, 'pointercancel', release);
    this.listen(this.viewport, 'touchstart', () => { this.touching = true; this.tween = null; this.updateMotion(); }, { passive: true });
    const endTouch = () => { this.touching = false; this.resumeAt = this.now() + 1000; this.applyPending(); this.updateMotion(); };
    this.listen(this.viewport, 'touchend', endTouch, { passive: true });
    this.listen(this.viewport, 'touchcancel', endTouch, { passive: true });
    this.listen(this.viewport, 'wheel', event => {
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) { this.tween = null; this.resumeAt = this.now() + 1000; }
    }, { passive: true });
    this.listen(this.viewport, 'scroll', () => this.readPosition(), { passive: true });
    this.listen(document, 'visibilitychange', () => { this.tween = null; this.updateMotion(); });
    this.listen(this.mobile, 'change', () => this.measure());
    this.listen(this.reduced, 'change', () => { this.tween = null; this.updateMotion(); });
    this.listen(this, 'shopify:block:select', event => {
      this.editorSelected = true; this.tween = null;
      const index = this.items.indexOf(event.target.closest('[data-wg-product-key]'));
      if (index >= 0) this.setPosition(index * this.stepPixels);
      this.updateMotion();
    });
    this.listen(this, 'shopify:block:deselect', () => { this.editorSelected = false; this.applyPending(); this.updateMotion(); });
    if ('ResizeObserver' in window) {
      this.resizeObserver = new window.ResizeObserver(() => this.measure()); this.resizeObserver.observe(this.viewport);
    } else this.listen(window, 'resize', () => this.measure());
    if ('IntersectionObserver' in window) {
      this.observer = new window.IntersectionObserver(entries => {
        this.inView = entries[0]?.isIntersecting ?? false; this.updateMotion();
      }); this.observer.observe(this);
    }
    this.setItems([...this.track.children].filter(item => !item.hasAttribute('data-wg-clone') && !item.hasAttribute('data-wg-filler')));
    this.dataset.motionVersion = '1.3.2'; this.load(this.run);
  }
  listen(target, event, handler, options) {
    target.addEventListener(event, handler, options);
    this.listeners.push(() => target.removeEventListener(event, handler, options));
  }
  now() { return window.performance.now(); }
  columns() { return this.mobile.matches ? Math.max(1, Math.min(2, Math.round(Number(this.dataset.mobileColumns) || 2))) : 4; }
  setItems(items) {
    this.stop(); this.tween = null;
    const store = window.WagglStore;
    const data = items.map(element => ({ handle: element.dataset.handle, title: element.dataset.title, series: element.dataset.series, element }));
    this.items = (store ? store.diverse(data) : data).map(value => value.element);
    for (const item of this.items) { item.hidden = false; item.style.removeProperty('order'); }
    this.track.replaceChildren(...this.items);
    this.position = 0; this.cycle = 0; this.beforeCount = 0;
    if (this.items.length) this.buildBuffers();
    if (this.controls) this.controls.hidden = this.items.length < 2;
    this.setAttribute('data-ready', ''); this.measure();
  }
  buildBuffers() {
    const before = document.createDocumentFragment(), after = document.createDocumentFragment();
    const count = 5; let serial = 0;
    const copy = original => {
      const element = original.cloneNode(true); element.hidden = false;
      element.setAttribute('data-wg-clone', ''); element.setAttribute('aria-hidden', 'true');
      const ids = new Map();
      for (const node of [element, ...element.querySelectorAll('*')]) {
        for (const attr of [...node.attributes]) if (attr.name.startsWith('data-shopify-editor')) node.removeAttribute(attr.name);
        if (node.id) { const id = node.id + '-buffer-' + (++serial); ids.set(node.id, id); node.id = id; }
        node.removeAttribute('view-event-payload');
      }
      for (const node of element.querySelectorAll('*')) {
        for (const name of ['aria-labelledby', 'aria-describedby', 'aria-controls', 'for', 'form']) {
          if (node.hasAttribute(name)) node.setAttribute(name, node.getAttribute(name).split(/\s+/).map(id => ids.get(id) || id).join(' '));
        }
        const href = node.getAttribute('href');
        if (href?.startsWith('#') && ids.has(href.slice(1))) node.setAttribute('href', '#' + ids.get(href.slice(1)));
        if (node.matches('a[href],button,input,select,textarea,[tabindex]')) node.setAttribute('tabindex', '-1');
      }
      return element;
    };
    for (let i = -count; i < 0; i++) before.append(copy(this.items[(i % this.items.length + this.items.length) % this.items.length]));
    for (let i = 0; i < count; i++) after.append(copy(this.items[i % this.items.length]));
    this.beforeCount = count; this.track.prepend(before); this.track.append(after);
  }
  measure() {
    if (!this.ready) return;
    const width = this.viewport.clientWidth;
    if (!width || !this.items.length) { this.stop(); return; }
    const phase = this.cycle ? this.position / this.cycle : 0;
    const gap = parseFloat(window.getComputedStyle(this.track).columnGap) || 0;
    const columns = this.columns();
    this.stepPixels = (width - gap * (columns - 1)) / columns + gap;
    this.start = this.beforeCount * this.stepPixels; this.cycle = this.items.length * this.stepPixels;
    this.tween = null; this.setPosition(phase * this.cycle); this.updateMotion();
  }
  setPosition(value) {
    if (!this.cycle) return;
    this.position = ((value % this.cycle) + this.cycle) % this.cycle;
    this.viewport.scrollLeft = this.start + this.position;
  }
  readPosition() {
    if (!this.ready || !this.cycle) return;
    const position = this.viewport.scrollLeft - this.start;
    if (Math.abs(position - this.position) < 0.5) return;
    this.position = ((position % this.cycle) + this.cycle) % this.cycle;
    if (position < 0 || position >= this.cycle) this.viewport.scrollLeft = this.start + this.position;
  }
  async load(run) {
    if (this.dataset.source === 'manual') { this.note('manual'); return; }
    try {
      const store = window.WagglStore;
      // Older sections have no data-catalog-url. Never treat a missing URL as an empty catalog.
      const root = window.Shopify?.routes?.root || '/';
      const url = this.dataset.source === 'collection' ? this.dataset.collectionUrl : (this.dataset.catalogUrl || root + 'collections/all?view=waggl-catalog');
      if (!url || !store) throw new Error('Catalog dependency missing');
      const all = await store.load(url);
      if (!this.ready || this.run !== run) return;
      const products = all.filter(product => this.dataset.excludeExamples === 'false' || !store.example(product));
      const items = products.map(product => {
        const item = document.createElement('li'); item.dataset.wgProductKey = product.id;
        item.dataset.handle = product.handle; item.dataset.title = product.title; item.dataset.series = product.series || '';
        item.innerHTML = typeof product.card === 'string' ? product.card : '';
        store.uniqueIDs(item, this.id); return item;
      }).filter(item => item.querySelector('.product-card-wrapper a[href]'));
      if (!items.length) throw new Error('Catalog contains no usable cards');
      this.pendingItems = items; this.applyPending();
    } catch {
      if (this.ready && this.run === run) this.note('fallback');
    }
  }
  applyPending() {
    if (!this.pendingItems || this.hovered || this.keyboardFocused || this.dragging || this.touching || this.editorSelected) return;
    const items = this.pendingItems; this.pendingItems = null; this.setItems(items); this.note('live');
  }
  note(state) {
    this.dataset.catalogState = state;
    const note = this.parentElement?.querySelector('[data-wg-carousel-note]');
    if (!note) return;
    note.textContent = state === 'fallback'
      ? '连续轮播 1.3.2：全店数据暂未读到，已保留现有商品。请检查 waggl-catalog.js、collection.waggl-catalog.liquid 和 waggl-product-data.liquid。'
      : '连续轮播 1.3.2：已载入 ' + this.items.length + ' 款商品；电脑四列宽度，悬停暂停、移开恢复。';
  }
  canAutoplay() {
    return this.ready && this.dataset.autoplay !== 'false' && this.items.length > 1 && this.cycle > 0 &&
      !this.reduced.matches && !this.hovered && !this.keyboardFocused && !this.dragging && !this.touching &&
      !this.editorSelected && this.inView && !document.hidden;
  }
  move(direction) {
    if (!this.cycle || this.items.length < 2) return;
    const target = this.position + direction * this.stepPixels;
    if (this.reduced.matches) this.setPosition(target);
    else this.tween = { from: this.position, to: target, time: this.now(), duration: 360 };
    this.updateMotion();
  }
  stop() { if (this.frame) window.cancelAnimationFrame(this.frame); this.frame = 0; this.lastTime = 0; }
  updateMotion() {
    if (!this.ready) return;
    if (this.tween || this.canAutoplay()) {
      if (!this.frame) { this.lastTime = 0; this.frame = window.requestAnimationFrame(time => this.tick(time)); }
    } else this.stop();
  }
  tick(time) {
    this.frame = 0;
    const delta = this.lastTime ? Math.min(time - this.lastTime, 64) : 0; this.lastTime = time;
    if (this.tween) {
      const progress = Math.min(Math.max((time - this.tween.time) / this.tween.duration, 0), 1);
      this.setPosition(this.tween.from + (this.tween.to - this.tween.from) * (1 - Math.pow(1 - progress, 3)));
      if (progress === 1) this.tween = null;
    } else if (this.canAutoplay() && time >= this.resumeAt) {
      const speed = Math.max(1, Number(this.mobile.matches ? this.dataset.mobileSpeed : this.dataset.speed) || (this.mobile.matches ? 20 : 25));
      this.setPosition(this.position + (this.dataset.direction === 'right' ? -1 : 1) * speed * delta / 1000);
    }
    if (this.tween || this.canAutoplay()) this.frame = window.requestAnimationFrame(next => this.tick(next));
    else this.lastTime = 0;
  }
  disconnectedCallback() {
    this.ready = false; this.run++; this.stop(); window.clearTimeout(this.focusTimer); this.tween = null; this.pendingItems = null;
    for (const remove of this.listeners || []) remove();
    this.resizeObserver?.disconnect(); this.observer?.disconnect();
    this.track?.querySelectorAll('[data-wg-clone]').forEach(item => item.remove());
    this.removeAttribute('data-ready');
  }
});
